<template>
  <div class="space-y-4">
    <section class="app-section">
      <div class="app-toolbar items-center justify-between">
        <h2 class="m-0 text-xl font-semibold">{{ filesTitle }}</h2>
      </div>

      <div class="app-toolbar">
        <input class="input-field-sm min-w-[220px]" type="text" v-model="search" placeholder="Поиск по имени файла" @input="fetchFiles" />
        <select class="input-field-sm" v-model="sortBy" @change="fetchFiles">
          <option value="created_at">По дате создания</option>
          <option value="updated_at">По дате обновления</option>
          <option value="filename">По имени</option>
        </select>
        <select class="input-field-sm" v-model="limit" @change="fetchFiles">
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>

      <div
        class="upload-area"
        @click="triggerFileInput"
        @dragover.prevent
        @drop.prevent="handleFileDrop"
        :class="{ uploading: isUploading }"
      >
        <input
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          style="display: none;"
        />
        <p v-if="!isUploading">Перетащите файл сюда или кликните для выбора</p>

        <div v-if="isUploading" class="upload-progress">
          <p>Загрузка файла...</p>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <span class="progress-text">{{ uploadProgress }}%</span>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="app-table min-w-[900px]">
          <thead>
            <tr>
              <th>Название</th>
              <th>Создано</th>
              <th>Перезагружено</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in files" :key="file.id" :class="{ 'new-file': isNewFile(file.updated_at) }">
              <td>{{ file.original_filename }}</td>
              <td>{{ formatDate(file.created_at) }}</td>
              <td>{{ formatDate(file.updated_at) }}</td>
              <td>
                <div class="flex flex-wrap gap-2">
                  <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="copyToClipboard(file.id)">UUID</button>
                  <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="downloadFile(file.id)">Скачать</button>
                  <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="copyLink(file.id, file.original_filename)">Ссылка</button>
                  <button class="button button-danger !h-8 !px-3 !py-0 text-xs" @click="deleteFile(file.id)">Удалить</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="notification" class="notification">{{ notification }}</div>
      <div class="pagination">
        <button class="button button-outline" @click="prevPage" :disabled="skip === 0">Назад</button>
        <button class="button button-outline" @click="nextPage">Вперед</button>
      </div>
    </section>

    <upload-modal v-if="isUploadModalOpen" @close="isUploadModalOpen = false" />
  </div>
</template>

<script>
import { uploadFileWithProgress, fetchFiles, fetchMyFiles, deleteFileApi } from '@/services/api';
import UploadModal from './UploadModal.vue';
import { isUserAdminForUi } from '../utils/user-role-override';

export default {
  name: 'FilesView',
  props: {
    currentUser: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    UploadModal,
  },
  computed: {
    canManageAll() {
      return Boolean(this.currentUser?.is_superuser) || isUserAdminForUi(this.currentUser);
    },
    filesTitle() {
      return this.canManageAll ? 'Файлы' : 'Мои файлы';
    }
  },
  watch: {
    currentUser: {
      deep: true,
      handler() {
        this.fetchFiles();
      }
    }
  },
  data() {
    return {
      files: [],
      notification: '',
      search: '',
      sortBy: 'updated_at',
      skip: 0,
      limit: 10,
      intervalId: null,
      fileName: '',
      fileDescription: '',
      isUploadModalOpen: false,
      isUploading: false,
      uploadProgress: 0,
    };
  },
  methods: {
    async fetchFiles() {
      try {
        this.files = this.canManageAll
          ? await fetchFiles(this.skip, this.limit, this.search, this.sortBy)
          : await fetchMyFiles(this.skip, this.limit, this.search, this.sortBy);
      } catch (error) {
        console.error('Ошибка при получении списка файлов:', error);
      }
    },
    formatDate(inputDate) {
      const now = new Date();
      const date = new Date(inputDate);
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) {
        return 'меньше минуты назад';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} мин назад`;
      } else if (date.toDateString() === now.toDateString()) {
        return `Сегодня, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (date.toDateString() === new Date(now - 86400000).toDateString()) {
        return `Вчера, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        const options = { day: 'numeric', month: 'long' };
        const formattedDate = date.toLocaleDateString('ru-RU', options);
        const year = date.getFullYear() !== now.getFullYear() ? `, ${date.getFullYear()}` : '';
        return `${formattedDate}${year}`;
      }
    },
    isNewFile(inputDate) {
      const now = new Date();
      const date = new Date(inputDate);
      const diffInSeconds = Math.floor((now - date) / 1000);
      return diffInSeconds < 60;
    },
    copyToClipboard(uuid) {
      navigator.clipboard.writeText(uuid);
      this.showNotification('UUID скопирован в буфер обмена');
    },
    downloadFile(uuid) {
      const API_URL = (process.env.VUE_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');
      window.location.href = `${API_URL}/files/${uuid}`;
    },
    copyLink(uuid, originalFilename) {
      const API_URL = (process.env.VUE_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');
      const link = `${API_URL}/uploads/${uuid}-${originalFilename}`;
      navigator.clipboard.writeText(link);
      this.showNotification('Ссылка скопирована в буфер обмена');
    },
    showNotification(message) {
      this.notification = message;
      const readingTime = Math.ceil(message.length / 1500 * 60) + 1; // Время в секундах
      setTimeout(() => {
        this.notification = '';
      }, readingTime * 1000); // Умножаем на 1000 для миллисекунд
    },
    nextPage() {
      this.skip = Number(this.skip) + Number(this.limit);
      this.fetchFiles();
    },
    prevPage() {
      if (this.skip > 0) {
        this.skip -= this.limit;
        this.fetchFiles();
      }
    },
    openUploadModal() {
      this.isUploadModalOpen = true;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadFile(file);
      }
    },
    handleFileDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.uploadFile(file);
      }
    },
    async uploadFile(file) {
      try {
        this.isUploading = true;
        this.uploadProgress = 0;
        
        await uploadFileWithProgress(
          file,
          (progress) => {
            this.uploadProgress = progress;
          },
          (error) => {
            console.error('Ошибка загрузки:', error);
            this.showNotification('Ошибка при загрузке файла: ' + error.message);
          }
        );
        
        this.showNotification('Файл успешно загружен');
        await this.fetchFiles();
        this.uploadProgress = 0;
        this.isUploading = false;
        
      } catch (error) {
        console.error(error);
        this.showNotification('Ошибка при загрузке файла: ' + (error.message || 'Неизвестная ошибка'));
        this.uploadProgress = 0;
        this.isUploading = false;
      }
    },
    async deleteFile(uuid) {
      try {
        await deleteFileApi(uuid);
        this.showNotification('Файл успешно удален');
        await this.fetchFiles();
      } catch (error) {
        console.error('Ошибка при удалении файла:', error);
        this.showNotification(`${error}`);
      }
    },
  },
  mounted() {
    this.fetchFiles();
    this.intervalId = setInterval(this.fetchFiles, 2000);
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  }
}
</script>

<style scoped>
.notification {
  margin-top: 10px;
  padding: 10px;
  background-color: hsl(var(--success));
  color: hsl(var(--success-foreground));
  border-radius: 5px;
  font-size: 14px;
}
.pagination {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}
.new-file {
  box-shadow: inset 3px 0 0 hsl(var(--warning));
}
.upload-area {
  border: 2px dashed hsl(var(--border));
  padding: 20px;
  text-align: center;
  cursor: pointer;
  margin: 20px 0;
  border-radius: 8px;
  transition: all 0.3s ease;
  @apply bg-muted/50 text-foreground;
}
.upload-area:hover {
  border-color: hsl(var(--primary));
  background-color: hsl(var(--primary) / 0.08);
}
.upload-area.uploading {
  border-color: hsl(var(--primary));
  background-color: hsl(var(--primary) / 0.05);
  cursor: default;
}
.upload-progress {
  margin-top: 10px;
  text-align: center;
}
.upload-progress p {
  margin: 0 0 10px 0;
  @apply text-foreground;
  font-weight: 500;
}
.progress-bar {
  height: 8px;
  @apply bg-muted;
  border-radius: 4px;
  overflow: hidden;
  margin: 10px 0;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.7));
  border-radius: 4px;
  transition: width 0.3s ease;
}
.progress-text {
  font-weight: 500;
  @apply text-muted-foreground;
  font-size: 14px;
}
</style> 
